import './ReviewShoutoutContent.scss';
import '../../../../CreateCommunityPost/styles.scss';

import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Button } from 'antd';
import {
  PiGear,
  PiPencilSimple,
} from 'react-icons/pi';
import { BottomSheet } from 'react-spring-bottom-sheet';
import TMIcon from 'tm-icons-library';

import Header from '../../../../../components/Header/Header';
import ProfileItem from '../../../../../components/ProfileItem/ProfileItem';
import { useTheme } from '../../../../../context/ThemeProvider';
import { IPostReqBodyPostSettings } from '../../../../../types/communityTypes';
import { TQShoutoutVisibility } from '../../../../../types/tqConnectTypes';
import {
  ITQShoutoutCategoryDetails,
  TQShoutoutVisibilityType,
} from '../../../../../types/tqConnectTypes/user';
import CommunityPostSettingsModel
  from '../../../../CreateCommunityPost/components/CommunityPostSettings';
import CreatePostSelectChannelModel
  from '../../../../CreateCommunityPost/components/CreatePostSelectChannelModel';
import ShoutoutPreview from '../ShoutoutPreview';
import ShoutoutVisibilitySettings, {
  ShoutoutSelectedChannel,
} from './ShoutoutVisibilitySettings';

interface Props {
  formValues: {
    description: string;
    category: ITQShoutoutCategoryDetails | null;
    coverImage: string;
    visibility: string;
  };
  givenBy?: { _id: string; name: string | null; profilePicUrl: string };
  givenTo?: { _id: string; name: string; profilePicUrl: string } | null;
  onEdit: () => void;
  onGiveShoutout: () => void;
  isSubmitting?: boolean;
  onVisibilityChange?: (visibility: TQShoutoutVisibilityType) => void;
  canPostToCommunity?: boolean;
  selectedChannelIds?: string[];
  onChannelsChange?: (channelIds: string[]) => void;
  postSettings?: IPostReqBodyPostSettings;
  onPostSettingsChange?: (settings: IPostReqBodyPostSettings) => void;
  postCaption?: string;
  defaultPostCaption?: string;
  onOpenPostCaptionEditor?: () => void;
}

const ReviewShoutoutContent: React.FC<Props> = ({
  formValues,
  givenBy,
  givenTo,
  onEdit,
  onGiveShoutout,
  isSubmitting = false,
  onVisibilityChange,
  canPostToCommunity = false,
  selectedChannelIds = [],
  onChannelsChange,
  postSettings = {
    disableComment: false,
    hideComment: false,
    hideLikes: false,
  },
  onPostSettingsChange,
  postCaption = '',
  defaultPostCaption = '',
  onOpenPostCaptionEditor,
}) => {
  const { colors: COLORS } = useTheme();
  const [visibilitySheetOpen, setVisibilitySheetOpen] = useState(false);
  const [channelSheetOpen, setChannelSheetOpen] = useState(false);
  const [postSettingsSheetOpen, setPostSettingsSheetOpen] = useState(false);
  const [localVisibility, setLocalVisibility] =
    useState<TQShoutoutVisibilityType>(
      formValues.visibility as TQShoutoutVisibilityType,
    );
  const [selectedChannelDetails, setSelectedChannelDetails] = useState<
    ShoutoutSelectedChannel[]
  >([]);

  useEffect(() => {
    if (selectedChannelIds.length === 0) {
      setSelectedChannelDetails([]);
    }
  }, [selectedChannelIds.length]);

  const openVisibilitySheet = useCallback(() => {
    setLocalVisibility(formValues.visibility as TQShoutoutVisibilityType);
    setVisibilitySheetOpen(true);
  }, [formValues.visibility]);

  const handleVisibilityDone = useCallback(() => {
    if (localVisibility !== 'channel-only') {
      onChannelsChange?.([]);
    }
    onVisibilityChange?.(localVisibility);
    setVisibilitySheetOpen(false);
  }, [localVisibility, onVisibilityChange, onChannelsChange]);

  const visibilityLabel =
    formValues.visibility === 'channel-only' ? 'Community' : 'Profile only';

  const handleChannelsSave = useCallback(
    (
      channels: {
        _id: string;
        name: string;
        communityProfilePicture?: string;
        communityProfilePictureType?: string;
      }[],
    ) => {
      setSelectedChannelDetails(channels);
      onChannelsChange?.(channels.map((c) => c._id));
      setChannelSheetOpen(false);
    },
    [onChannelsChange],
  );

  const handleRemoveChannel = useCallback(
    (channelId: string) => {
      setSelectedChannelDetails((prev) => {
        const next = prev.filter((c) => c._id !== channelId);
        onChannelsChange?.(next.map((c) => c._id));
        return next;
      });
    },
    [onChannelsChange],
  );

  const displayChannels =
    selectedChannelDetails.length > 0 ? selectedChannelDetails : [];

  const showPostSettingsButton = useMemo(() => {
    return (
      formValues.visibility === TQShoutoutVisibility.CHANNEL_ONLY &&
      selectedChannelIds.length > 0
    );
  }, [formValues.visibility, selectedChannelIds.length]);

  const handlePostSettingsSave = useCallback(
    (settings: IPostReqBodyPostSettings) => {
      onPostSettingsChange?.(settings);
      setPostSettingsSheetOpen(false);
    },
    [onPostSettingsChange],
  );

  const displayCaption = postCaption || defaultPostCaption;

  return (
    <div className="review-shoutout-content">
      <div className="review-shoutout-content__body">
        <ProfileItem
          className="review-shoutout-content__header"
          avatar={givenBy?.profilePicUrl}
          avatarSize={48}
          title={
            <>
              <span className="review-shoutout-content__header__sender">
                {givenBy?.name ?? 'You'}
              </span>{' '}
              is giving shoutout to{' '}
              <span className="review-shoutout-content__header__recipient">
                {givenTo?.name ?? 'Someone'}
              </span>
            </>
          }
          subtitle={
            <div className="review-shoutout-content__header__subtitle">
              <button
                type="button"
                className="review-shoutout-content__visibility"
                onClick={openVisibilitySheet}>
                <TMIcon
                  name={
                    formValues.visibility === 'profile-only'
                      ? 'person'
                      : 'people'
                  }
                  size={16}
                  color={COLORS.ICON}
                  style={{ flexShrink: 0 }}
                />
                <span className="review-shoutout-content__visibility__label">
                  {visibilityLabel}
                </span>
                <TMIcon
                  name="chevron-down"
                  size={16}
                  color={COLORS.ICON}
                  style={{ flexShrink: 0 }}
                />
              </button>
              {showPostSettingsButton ? (
                <button
                  type="button"
                  className="review-shoutout-content__visibility"
                  onClick={() => setPostSettingsSheetOpen(true)}>
                  <PiGear
                    size={16}
                    color={COLORS.ICON}
                    style={{ flexShrink: 0 }}
                  />
                  <span className="review-shoutout-content__visibility__label">
                    Post Settings
                  </span>
                  <TMIcon
                    name="chevron-down"
                    size={16}
                    color={COLORS.ICON}
                    style={{ flexShrink: 0 }}
                  />
                </button>
              ) : null}
            </div>
          }
          titleStyle={{ fontWeight: 400 }}
          subtitleStyle={{ width: '100%' }}
        />

        {showPostSettingsButton ? (
          <div className="review-shoutout-content__caption-section">
            <span className="review-shoutout-content__caption-section__label">
              Post description
            </span>
            <button
              type="button"
              className="review-shoutout-content__caption-trigger"
              onClick={onOpenPostCaptionEditor}>
              <span className="review-shoutout-content__caption-trigger__icon">
                <PiPencilSimple size={16} color={COLORS.ICON} />
              </span>
              <span className="review-shoutout-content__caption-trigger__text">
                {displayCaption.trim()
                  ? displayCaption
                  : 'Add a caption for your community post...'}
              </span>
            </button>
          </div>
        ) : null}

        <div className="review-shoutout-content__card">
          <ShoutoutPreview
            coverImage={formValues.coverImage}
            category={formValues.category as ITQShoutoutCategoryDetails}
            description={formValues.description}
          />
          <Button
            className="siteBtn darkBtn review-shoutout-content__edit-btn"
            icon={<PiPencilSimple size={16} color={COLORS.SURFACE_PAGE} />}
            onClick={onEdit}>
            Edit
          </Button>
        </div>
      </div>

      <div className="review-shoutout-content__footer">
        <Button
          className="siteBtn accentBtnV1 review-shoutout-content__give-btn"
          onClick={onGiveShoutout}
          loading={isSubmitting}
          block>
          Give Shoutout
        </Button>
      </div>

      <BottomSheet
        open={visibilitySheetOpen}
        onDismiss={() => {
          setVisibilitySheetOpen(false);
          setChannelSheetOpen(false);
          if (formValues.visibility !== 'channel-only') {
            setSelectedChannelDetails([]);
            onChannelsChange?.([]);
          }
        }}
        className="review-shoutout-content__visibility-sheet"
        header={
          <div className="review-shoutout-content__visibility-sheet__header">
            <Header
              title="Where to post shoutout?"
              canGoBack={false}
              noBorder
              actionItems={[
                <Button
                  className="siteBtn accentBtnV1 "
                  onClick={handleVisibilityDone}>
                  Done
                </Button>,
              ]}
            />
          </div>
        }>
        <div className="review-shoutout-content__visibility-sheet__content">
          <ShoutoutVisibilitySettings
            visibility={localVisibility}
            canPostToCommunity={canPostToCommunity}
            onVisibilityChange={setLocalVisibility}
            onDone={handleVisibilityDone}
            showFooter={false}
            selectedChannels={displayChannels}
            onSelectChannels={() => setChannelSheetOpen(true)}
            onRemoveChannel={handleRemoveChannel}
          />
        </div>
      </BottomSheet>

      <CreatePostSelectChannelModel
        show={channelSheetOpen}
        closeModal={() => setChannelSheetOpen(false)}
        handleSave={handleChannelsSave}
        selectedIdList={selectedChannelIds}
      />

      <CommunityPostSettingsModel
        show={postSettingsSheetOpen}
        closeModal={() => setPostSettingsSheetOpen(false)}
        handleSave={handlePostSettingsSave}
        postSettings={postSettings}
      />
    </div>
  );
};

export default memo(ReviewShoutoutContent);
